/** Shared text used by every app. App-specific text lives in `@tnn/domain` next to each module. */
export const th = {
  apps: {
    // App names are English in every language (packages/core/src/apps.ts); `cash` is a module, named in Thai
    portal: 'Thananan OS',
    tms: 'TMS',
    accounting: 'Accounting',
    hr: 'HR',
    payroll: 'Payroll',
    admin: 'Admin',
    company: 'Company',
    driver: 'Driver',
    drive: 'Drive',
    boards: 'Boards',
    cash: 'เงินสด',
    access: 'การเข้าใช้งาน',
    switcher: 'แอพทั้งหมด',
    other: 'แอพอื่น',
  },
  actions: {
    save: 'บันทึก',
    saveAndNew: 'บันทึกและสร้างใหม่',
    cancel: 'ยกเลิก',
    close: 'ปิด',
    create: 'สร้าง',
    edit: 'แก้ไข',
    delete: 'ลบ',
    duplicate: 'ทำสำเนา',
    view: 'ดู',
    back: 'กลับ',
    more: 'เพิ่มเติม',
    search: 'ค้นหา',
    filter: 'ตัวกรอง',
    clearFilters: 'ล้างตัวกรอง',
    refresh: 'รีเฟรช',
    import: 'นำเข้า',
    export: 'ส่งออก',
    print: 'พิมพ์',
    retry: 'ลองอีกครั้ง',
    confirm: 'ยืนยัน',
    columns: 'คอลัมน์',
    selectAll: 'เลือกทั้งหมด',
    done: 'เสร็จสิ้น',
    next: 'ถัดไป',
    previous: 'ก่อนหน้า',
    openMenu: 'เปิดเมนู',
  },
  status: {
    loading: 'กำลังโหลด…',
    saving: 'กำลังบันทึก…',
    saved: 'บันทึกแล้ว',
    created: 'สร้าง{{name}}แล้ว',
    updated: 'บันทึก{{name}}แล้ว',
    deleted: 'ลบ{{name}}แล้ว',
    offline: 'ไม่ได้เชื่อมต่ออินเทอร์เน็ต',
    demo: 'ข้อมูลทดลอง',
  },
  empty: {
    title: 'ยังไม่มี{{name}}',
    body: 'เริ่มจากสร้างรายการแรก หรือนำเข้าจากไฟล์ Excel',
    bodyCreate: 'รายการที่สร้างจะแสดงที่นี่',
    noResults: 'ไม่พบรายการที่ตรงกับเงื่อนไข',
    noResultsBody: 'ลองเปลี่ยนคำค้นหาหรือล้างตัวกรอง',
  },
  errors: {
    generic: 'เกิดข้อผิดพลาด กรุณาลองอีกครั้ง',
    network: 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ตรวจสอบอินเทอร์เน็ตแล้วลองอีกครั้ง',
    forbidden: 'คุณไม่มีสิทธิ์ทำรายการนี้',
    forbiddenApp: 'บัญชีนี้ยังไม่มีสิทธิ์ใช้แอพนี้ ติดต่อผู้ดูแลระบบเพื่อขอสิทธิ์',
    notFound: 'ไม่พบข้อมูลที่ต้องการ',
    validation: 'กรุณาตรวจสอบข้อมูลที่ไฮไลต์',
    conflict: 'ข้อมูลนี้มีอยู่แล้ว',
    pageNotFound: 'ไม่พบหน้านี้',
  },
  confirm: {
    deleteTitle: 'ลบ{{name}}?',
    deleteBody: 'ลบ {{label}} แล้วจะกู้คืนไม่ได้',
    leaveTitle: 'ยังไม่ได้บันทึก',
    leaveBody: 'การแก้ไขที่ยังไม่บันทึกจะหายไป ต้องการออกจากหน้านี้หรือไม่',
    leave: 'ออกโดยไม่บันทึก',
    stay: 'อยู่ต่อ',
  },
  list: {
    range: '{{from}}–{{to}} จาก {{total}}',
    rowsPerPage: 'แถวต่อหน้า',
    selected: 'เลือก {{count}} รายการ',
    all: 'ทั้งหมด',
    searchPlaceholder: 'ค้นหา{{name}}',
    density: 'ความหนาแน่น',
    resetColumns: 'คืนค่าคอลัมน์',
    dateRange: 'ช่วงวันที่',
    today: 'วันนี้',
    selectRow: 'เลือกแถวที่ {{n}}',
    last7Days: '7 วันล่าสุด',
    between: 'ระหว่าง',
    allDates: 'ทุกวัน',
    previousPage: 'หน้าก่อน',
    nextPage: 'หน้าถัดไป',
  },
  /** Thai banks (`banks` in @tnn/domain): an employee's pay account, a supplier's */
  banks: {
    KBANK: 'กสิกรไทย',
    SCB: 'ไทยพาณิชย์',
    BBL: 'กรุงเทพ',
    KTB: 'กรุงไทย',
    BAY: 'กรุงศรีอยุธยา',
    TTB: 'ทหารไทยธนชาต',
    GSB: 'ออมสิน',
    BAAC: 'ธ.ก.ส.',
    GHB: 'อาคารสงเคราะห์',
    UOB: 'ยูโอบี',
    CIMB: 'ซีไอเอ็มบี ไทย',
    KKP: 'เกียรตินาคินภัทร',
    LHB: 'แลนด์ แอนด์ เฮ้าส์',
    TISCO: 'ทิสโก้',
    ICBC: 'ไอซีบีซี (ไทย)',
  },
  party: {
    juristic: 'นิติบุคคล',
    individual: 'บุคคลธรรมดา',
    type: 'ประเภท',
    noWithholding: 'บุคคลธรรมดาหัก ณ ที่จ่ายไม่ได้',
    idCard: 'เลขประจำตัวประชาชน',
  },
  doc: {
    fields: {
      docstatus: 'สถานะเอกสาร',
      amendedFrom: 'แก้ไขจาก',
      submittedAt: 'เสนอเมื่อ',
      submittedBy: 'ผู้เสนอ',
      cancelledAt: 'ยกเลิกเมื่อ',
      cancelledBy: 'ผู้ยกเลิก',
      cancelReason: 'เหตุผลที่ยกเลิก',
    },
    status: { draft: 'ร่าง', submitted: 'เสนอแล้ว', cancelled: 'ยกเลิก' },
    /** Approved by whoever put it up: a super admin's own (`approvesOwn`) */
    selfApproved: 'อนุมัติเอง',
    draftTitle: '{{name}} (ร่าง)',
    actions: {
      saveDraft: 'บันทึกร่าง',
      submit: 'เสนอ',
      saveAndSubmit: 'บันทึกและเสนอ',
      cancel: 'ยกเลิกเอกสาร',
      amend: 'แก้ไขเป็นฉบับใหม่',
      editAfterSubmit: 'แก้ไขหลังเสนอ',
      stopEditing: 'เลิกแก้ไข',
    },
    confirm: {
      submitTitle: 'เสนอ{{name}}?',
      submitBody: 'เมื่อเสนอแล้วจะได้เลขที่เอกสาร และแก้ไขไม่ได้ ถ้าผิดต้องยกเลิกแล้วแก้ไขเป็นฉบับใหม่',
      cancelTitle: 'ยกเลิก{{name}} {{label}}?',
      cancelBody: 'เอกสารยังเก็บไว้เป็นประวัติ ผลของเอกสารนี้จะถูกยกเลิก และแก้ไขเป็นฉบับใหม่ได้ภายหลัง',
      reason: 'เหตุผลที่ยกเลิก',
      reasonRequired: 'กรุณาระบุเหตุผล',
      submitManyTitle: 'เสนอ{{name}} {{count}} รายการ?',
    },
    done: {
      submit: 'เสนอ{{name}} {{label}} แล้ว',
      cancel: 'ยกเลิก{{name}} {{label}} แล้ว',
      amend: 'สร้างร่างฉบับใหม่จาก {{label}} แล้ว',
      submitMany: 'เสนอแล้ว {{count}} รายการ',
      submitManyFailed: 'เสนอได้ {{done}} รายการ ไม่สำเร็จ {{failed}} รายการ: {{reason}}',
    },
    banner: {
      draft: 'ร่าง: ยังไม่มีผล ตรวจแล้วกด "เสนอ"',
      submitted: 'เสนอแล้วเมื่อ {{at}} โดย {{by}} แก้ไขไม่ได้ ถ้าผิดให้ยกเลิกแล้วแก้ไขเป็นฉบับใหม่',
      submittedEditable: 'เสนอแล้วเมื่อ {{at}} โดย {{by}} ถ้าต้องแก้ กด "แก้ไขหลังเสนอ" ระบบบันทึกทุกการแก้ไข',
      editingAfterSubmit: 'กำลังแก้ไขหลังเสนอ แก้ได้{{fields}} ทุกการแก้ไขถูกบันทึกในความเคลื่อนไหว และนับเป็น "แก้ไขหลังเสนอ"',
      allFields: 'ทุกช่อง',
      cancelled: 'ยกเลิกเมื่อ {{at}} โดย {{by}}',
      reason: 'เหตุผล: {{reason}}',
      amendedFrom: 'แก้ไขจาก',
    },
  },
  file: {
    attach: 'แนบรูปหรือไฟล์',
    view: 'ดูไฟล์',
    remove: 'เอาไฟล์ออก',
    tooBig: 'ไฟล์ใหญ่เกิน 10 MB',
    failed: 'อัปโหลดไม่สำเร็จ ลองอีกครั้ง',
    uploading: 'กำลังอัปโหลด…',
  },
  /** A field done on a phone (app-kit tasks/handoff) */
  task: {
    thisDevice: 'เลือกไฟล์จากเครื่องนี้',
    takeOrPick: 'ถ่ายรูปหรือเลือกไฟล์',
    askSomeone: 'ขอให้คนอื่นทำ…',
    signature: 'เซ็นชื่อ',
    askTitle: 'ขอให้คนอื่นทำ: {{label}}',
    person: 'ขอใคร',
    personPlaceholder: 'พิมพ์ชื่อเพื่อค้นหา',
    note: 'ข้อความถึงเขา (ไม่บังคับ)',
    askHint: 'ถ้าเขาตั้งให้รับงานจากคุณอัตโนมัติ จะเด้งแจ้งเตือนทันที ไม่อย่างนั้นจะรออยู่ใน "คำขอ" ของเขาจนกว่าจะกดรับ',
    send: 'ส่งคำขอ',
    waitingOther: 'รอ {{name}} ทำให้',
    requested: 'ส่งคำขอถึง {{name}} แล้ว รอเขากดรับ',
    declined: '{{name}} ปฏิเสธคำขอ',
    cancelled: 'ยกเลิกคำขอแล้ว',
    expired: 'คำขอหมดเวลาแล้ว ขอใหม่ได้',
    done: 'ได้รับแล้ว',
    cancel: 'ยกเลิก',
    dismiss: 'ตกลง',
    failed: 'ส่งคำขอไม่สำเร็จ ลองอีกครั้ง',
    /** Doing one on the device that does it (app-kit tasks/work.tsx) */
    work: { camera: 'ถ่ายรูป', choose: 'เลือกรูปหรือไฟล์', send: 'ส่ง ({{count}})', sendSignature: 'ส่งลายเซ็น' },
  },
  /** The portable screen (app-kit screen/, its own site apps/screen): pairing it, what waits there, and its own page */
  screen: {
    name: 'จอพกพา',
    pair: 'เชื่อมจอพกพา',
    pairTitle: 'เชื่อมจอพกพา',
    pairHint: 'เปิดแอปจอพกพา ({{url}}) บนแท็บเล็ตหรือมือถือ แล้วสแกน QR นี้ หรือพิมพ์รหัสด้านล่าง',
    scanThis: 'QR สำหรับเชื่อมจอพกพา',
    expiresIn: 'ใช้ได้อีก {{seconds}} วินาที',
    spent: 'รหัสนี้หมดเวลาแล้ว',
    newCode: 'ขอรหัสใหม่',
    replaces: 'จอเดิม ({{name}}) จะเลิกเชื่อมเมื่อเชื่อมจอใหม่',
    useThisDevice: 'ใช้เครื่องนี้เป็นจอพกพา',
    pairedWith: 'เชื่อม {{name}} เป็นจอพกพาแล้ว',
    failed: 'เชื่อมจอไม่สำเร็จ ลองอีกครั้ง',
    attachThere: 'ถ่ายที่จอพกพา',
    pairToAttach: 'ถ่ายที่จอพกพา…',
    signThere: 'เซ็นที่จอพกพา',
    waiting: 'รอที่จอพกพา',
    waitingSign: 'ส่งไปที่จอพกพาแล้ว รอเซ็น',
    waitingOn: 'ขึ้นที่ {{name}} แล้ว',
    closed: '{{name}} ไม่ได้เปิดอยู่ จะขึ้นเมื่อเปิดจอ',
    doHere: 'ทำที่นี่แทน',
    signHere: 'เซ็นที่จอนี้แทน',
    again: 'ให้เซ็นใหม่ที่จอพกพา',
    device: { androidPhone: 'มือถือ Android', androidTablet: 'แท็บเล็ต Android', computer: 'คอมพิวเตอร์' },
    pairHere: 'เชื่อมจอนี้',
    pairHereHint: 'ที่เครื่องของคุณ กด "{{button}}" (จอลงเวลาของสาขา: กด "{{device}}" ที่จอนั้นในแอพ HR) แล้วสแกน QR ที่ขึ้นด้วยจอนี้ หรือพิมพ์รหัส 6 หลัก',
    pairDevice: 'เชื่อมเครื่อง',
    clockQr: 'QR ลงเวลา',
    clockHint: 'สแกนด้วยกล้องมือถือของคุณเพื่อลงเวลาเข้าหรือออกงาน',
    clockOnce: 'QR ใช้ได้ครั้งเดียว เปลี่ยนใหม่ทุกครั้งที่มีคนสแกน',
    clockedIn: '{{name}} เข้างาน {{time}}',
    clockedOut: '{{name}} ออกงาน {{time}}',
    scan: 'สแกน QR',
    code: 'รหัส 6 หลัก',
    orCode: 'หรือพิมพ์รหัส 6 หลัก',
    pairButton: 'เชื่อม',
    noCamera: 'เปิดกล้องไม่ได้ พิมพ์รหัสแทน',
    yours: 'จอพกพาของ {{name}}',
    welcome: 'ยินดีต้อนรับ',
    idle: 'เมื่อมีเอกสารให้เซ็นหรือรูปให้ถ่าย จะขึ้นที่จอนี้',
    tapFullscreen: 'แตะเพื่อเต็มจอ',
    fullscreen: 'เต็มจอ',
    exitFullscreen: 'ออกจากเต็มจอ',
    confirm: 'ยืนยันลายเซ็น',
    thanks: 'ขอบคุณ ได้รับแล้ว',
    more: 'รออีก {{count}} รายการ',
    leave: 'เลิกเชื่อมจอนี้',
    leaveConfirm: 'เลิกเชื่อมจอนี้กับ {{name}}? ต้องสแกนใหม่ถึงจะใช้ได้อีก',
    hint: 'แท็บเล็ตหรือมือถือที่ลูกค้าเซ็นชื่อ และถ่ายรูปแนบเอกสาร ขณะที่คุณทำงานที่คอม',
    none: 'ยังไม่ได้เชื่อมจอ',
    pairedAt: 'เชื่อมเมื่อ {{at}}',
    online: 'เปิดอยู่',
    offline: 'ไม่ได้เปิด',
    unpair: 'เลิกเชื่อม',
  },
  activity: {
    title: 'ความเคลื่อนไหว',
    comment: 'ความคิดเห็น',
    placeholder: 'เขียนความคิดเห็น… (Ctrl + Enter เพื่อส่ง)',
    send: 'ส่งความคิดเห็น',
    edited: 'แก้ไขแล้ว',
    deleteTitle: 'ลบความคิดเห็นนี้?',
    deleteBody: 'ลบแล้วกู้คืนไม่ได้',
    listChanged: 'เปลี่ยนรายการ',
    afterSubmit: 'แก้หลังเสนอ',
    via: '(ผ่าน {{name}})',
    showAll: 'ดูทั้งหมด ({{count}})',
    editedAfterSubmit: 'แก้ไขหลังเสนอ {{count}} ฟิลด์',
    editedAfterSubmitTitle: 'แก้ไขหลังเสนอ {{count}} ฟิลด์ จาก {{edits}} ครั้ง',
  },
  form: {
    required: 'จำเป็น',
    optional: 'ไม่บังคับ',
    selectPlaceholder: 'เลือก',
    searchPlaceholder: 'พิมพ์เพื่อค้นหา',
    suggestHint: 'พิมพ์ใหม่ได้ หรือเลือกจากที่เคยใช้',
    area: 'ตำบล / อำเภอ / จังหวัด',
    province: 'จังหวัด',
    areaHint: 'พิมพ์ตำบล อำเภอ จังหวัด หรือรหัสไปรษณีย์',
    coverageHint: 'พิมพ์ชื่อจังหวัดหรืออำเภอ',
    excludedHint: 'พิมพ์ชื่ออำเภอหรือตำบล',
    noOptions: 'ไม่พบตัวเลือก',
    yes: 'ใช่',
    no: 'ไม่ใช่',
    newRecord: '{{name}}ใหม่',
    editRecord: 'แก้ไข{{name}}',
    step: 'ขั้นที่ {{step}} จาก {{steps}}',
    toReview: 'กลับไปตรวจทาน',
    copiedFrom: 'สำเนาจาก {{label}} ยังไม่ได้บันทึก',
    taken: '{{field}}นี้มีอยู่แล้วที่',
    takenMore: 'และอีก {{count}} รายการ',
  },
  import: {
    title: 'นำเข้า{{name}}',
    stepUpload: 'เลือกไฟล์',
    stepMap: 'จับคู่คอลัมน์',
    stepReview: 'ตรวจสอบ',
    stepDone: 'เสร็จสิ้น',
    drop: 'ลากไฟล์ Excel หรือ CSV มาวางที่นี่',
    dropHint: 'หรือคลิกเพื่อเลือกไฟล์ (.xlsx, .csv สูงสุด 5,000 แถว)',
    template: 'ดาวน์โหลดไฟล์ตัวอย่าง',
    column: 'คอลัมน์ในไฟล์',
    field: 'ข้อมูลในระบบ',
    ignore: 'ไม่นำเข้า',
    mode: 'วิธีนำเข้า',
    modeCreate: 'เพิ่มเป็นรายการใหม่ทั้งหมด',
    modeUpsert: 'อัปเดตรายการที่มีอยู่ (จับคู่ด้วย{{key}})',
    rows: '{{count}} แถว',
    valid: 'พร้อมนำเข้า {{count}} แถว',
    invalid: 'มีปัญหา {{count}} แถว',
    row: 'แถว {{row}}',
    fixAndRetry: 'แก้ไขในไฟล์แล้วอัปโหลดใหม่ หรือนำเข้าเฉพาะแถวที่ถูกต้อง',
    importValid: 'นำเข้า {{count}} แถว',
    result: 'เพิ่มใหม่ {{created}} รายการ อัปเดต {{updated}} รายการ',
    unsupported: 'ไฟล์นี้อ่านไม่ได้ กรุณาใช้ .xlsx หรือ .csv',
    allOrNothing: 'ต้องแก้ทุกแถวให้ถูกก่อน ระบบจะบันทึกทั้งหมดพร้อมกัน',
    checking: 'กำลังตรวจ…',
    imported: 'นำเข้าแล้ว {{count}} รายการ',
    chooseFile: 'เลือกไฟล์ Excel',
  },
  export: {
    title: 'ส่งออก{{name}}',
    excel: 'Excel (.xlsx)',
    csv: 'CSV (.csv)',
    scopeFiltered: 'ตามตัวกรองตอนนี้ ({{count}} รายการ)',
    scopeSelected: 'เฉพาะที่เลือก ({{count}} รายการ)',
    preparing: 'กำลังเตรียมไฟล์…',
    ready: 'ส่งออกแล้ว {{count}} รายการ',
  },
  print: {
    title: 'พิมพ์',
    list: 'พิมพ์รายการ',
    printedAt: 'พิมพ์เมื่อ {{at}}',
    printedBy: 'พิมพ์เมื่อ {{at}} โดย {{name}}',
    time: 'ครั้งที่ {{count}}',
    reprint: 'พิมพ์ซ้ำ ครั้งที่ {{count}}',
    page: 'หน้า',
  },
  /** A report's page (`ReportPage`) and an app's page of reports */
  report: {
    title: 'รายงาน',
    pick: 'นับและรวมยอดตามช่วงวันที่ จัดกลุ่มและกรองได้เอง กดแถวเพื่อดูรายการ',
    noneYours: 'ยังไม่มีรายงานที่คุณเปิดดูได้',
    groupBy: 'จัดกลุ่มตาม',
    byDay: 'รายวัน',
    byMonth: 'รายเดือน',
    count: 'จำนวน',
    total: 'รวม',
    none: 'ไม่ระบุ',
    thisMonth: 'เดือนนี้',
    lastMonth: 'เดือนที่แล้ว',
    thisYear: 'ปีนี้',
    allFilters: 'ทุกรายการ',
    more: 'แสดง {{count}} กลุ่มที่มากที่สุด',
    openList: 'ดูรายการของ {{name}}',
  },
  scan: { starting: 'กำลังเปิดกล้อง… ถ้ามีหน้าต่างขออนุญาต ให้กดอนุญาต', close: 'ปิดกล้อง', open: 'สแกนด้วยกล้อง' },
  signature: {
    mine: 'ลายเซ็นของฉัน',
    mineHint: 'ใช้เซ็นเอกสารในระบบ เช่น ยินยอมหักเงินเบิกล่วงหน้า และเอกสารที่คุณออก เซ็นบนกระดาษขาวแล้วถ่ายรูป ให้เหมือนลายเซ็นจริงของคุณ',
    drawnKept: 'ลายเซ็นนี้เซ็นบนจอ ควรเปลี่ยนเป็นรูปถ่ายลายเซ็นบนกระดาษ',
    none: 'ยังไม่ได้ตั้งลายเซ็น',
    set: 'ตั้งลายเซ็น',
    change: 'เปลี่ยนลายเซ็น',
    remove: 'ลบลายเซ็น',
    removeConfirm: 'ลบลายเซ็นของคุณไหม เอกสารที่เซ็นไปแล้วยังมีลายเซ็นเดิมอยู่',
    saved: 'บันทึกลายเซ็นแล้ว',
    removed: 'ลบลายเซ็นแล้ว',
    updated: 'ตั้งเมื่อ {{at}}',
    draw: 'เซ็นบนจอ',
    photo: 'ถ่ายรูปลายเซ็น',
    drawHint: 'ใช้นิ้วหรือปากกาเซ็นในกรอบ',
    signHere: 'ลงชื่อ',
    clear: 'ล้าง',
    photoTitle: 'ถ่ายรูปลายเซ็นบนกระดาษ',
    photoTips: 'เซ็นด้วยปากกาสีเข้มบนกระดาษขาว ถ่ายให้ลายเซ็นอยู่กลางรูป ระบบลบเงาและพื้นหลังให้',
    choosePhoto: 'ถ่ายรูปหรือเลือกรูป',
    otherPhoto: 'เลือกรูปอื่น',
    cleaning: 'กำลังปรับลายเซ็นให้คมชัด…',
    notFound: 'ไม่พบลายเซ็นในรูป ลองถ่ายใหม่ให้ลายเซ็นอยู่กลางรูปและมีแสงพอ',
    strength: 'ความเข้มของเส้น',
    use: 'ใช้ลายเซ็นนี้',
    tapToSign: 'แตะเพื่อเซ็น',
    signAgain: 'เซ็นใหม่',
    useMine: 'ใช้ลายเซ็นของฉัน',
    keepAsMine: 'เก็บเป็นลายเซ็นของฉัน ครั้งหน้าแตะครั้งเดียว',
    method: { stored: 'ลายเซ็นที่เก็บไว้', drawn: 'เซ็นบนจอ', photo: 'จากรูปถ่าย', paper: 'เซ็นบนกระดาษ' },
    signing: { signed: 'เซ็นแล้ว', unsigned: 'ยังไม่เซ็น' },
    via: { app: 'ในแอป', link: 'ผ่านลิงก์', device: 'บนเครื่องของพนักงาน', standing: 'ตามที่ผู้มีอำนาจลงนามอนุญาตไว้' },
    required: 'กรุณาเซ็นชื่อ',
    failed: 'บันทึกลายเซ็นไม่สำเร็จ ลองอีกครั้ง',
    alt: 'ลายเซ็น',
    date: 'วันที่',
    reminder: 'คุณยังไม่ได้ตั้งลายเซ็น เอกสารที่คุณออกจะเว้นช่องลงชื่อไว้ให้เซ็นด้วยมือ',
  },
  /** A document's signing box: the line someone outside signs, here, on a link or on paper */
  signing: {
    here: 'เซ็นที่นี่',
    hereHint: 'บนจอนี้ หรือจอพกพาที่จับคู่ไว้',
    link: 'ส่งลิงก์',
    linkHint: 'ให้เซ็นบนมือถือของเขาเอง',
    paper: 'เซ็นบนกระดาษแล้ว',
    paperHint: 'ถ่ายรูปเอกสารที่เซ็นเก็บไว้',
    name: 'ชื่อผู้เซ็น',
    title: 'ตำแหน่ง',
    needSignature: 'กรุณาเซ็นในกรอบ',
    photo: 'ถ่ายรูปเอกสารที่เซ็นแล้ว',
    photoHint: 'ถ่ายหน้าที่มีลายเซ็น ให้เห็นเลขที่เอกสาร',
    retake: 'ถ่ายใหม่',
    needPhoto: 'ถ่ายรูปเอกสารที่เซ็นแล้วก่อน',
    pagePhoto: 'รูปเอกสารที่เซ็นบนกระดาษ',
    save: 'บันทึก',
    saved: 'บันทึกลายเซ็นแล้ว',
    keepTitle: 'สแกนเก็บสำเนา',
    keepHint: 'สแกน QR นี้ด้วยมือถือ เปิดดูและดาวน์โหลด PDF ได้ถึง {{date}}',
    done: 'เสร็จ',
    linkTitle: 'ส่งลิงก์ให้เซ็น',
    linkIntro: 'ส่งลิงก์ทาง LINE หรือให้สแกน QR นี้ เปิดดูเอกสาร เซ็น และดาวน์โหลด PDF ได้โดยไม่ต้องมีบัญชี',
    copy: 'คัดลอกลิงก์',
    copied: 'คัดลอกแล้ว',
    copyMessage: 'คัดลอกข้อความ',
    share: 'แชร์',
    expires: 'ลิงก์ใช้ได้ถึง {{date}} และเซ็นได้ครั้งเดียว',
    message: '{{company}} ส่ง{{document}} เลขที่ {{number}} ให้เซ็น เปิดดูและดาวน์โหลดได้ที่ {{url}}',
    waiting: 'ส่งลิงก์แล้ว รอเซ็น',
    viewed: 'เปิดดูแล้ว {{at}}',
    notViewed: 'ยังไม่เปิดดู',
    showLink: 'ดูลิงก์',
    revoke: 'ยกเลิกลิงก์',
    revoked: 'ยกเลิกลิงก์แล้ว ลิงก์เดิมเปิดไม่ได้อีก',
    changed: 'แก้หลังเซ็น',
    changedHint: 'เอกสารถูกแก้หลังจากเซ็น ดูว่าแก้อะไรได้ที่ประวัติของเอกสาร',
    onPaper: 'ลงนามบนต้นฉบับกระดาษ',
  },
  auth: {
    signInTitle: 'เข้าสู่ระบบ',
    signInSubtitle: 'ใช้บัญชีเดียวกับทุกแอพในบริษัท',
    username: 'ชื่อผู้ใช้',
    password: 'รหัสผ่าน',
    signIn: 'เข้าสู่ระบบ',
    signOut: 'ออกจากระบบ',
    invalid: 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง',
    sessionExpired: 'หมดเวลาการใช้งาน กรุณาเข้าสู่ระบบอีกครั้ง',
    forgot: 'ลืมรหัสผ่าน ขอลิงก์ตั้งรหัสผ่านใหม่จากหัวหน้า',
    noAccount: 'ยังไม่มีบัญชี?',
    signUp: 'ขอเปิดบัญชี',
    pending: 'บัญชีนี้รออนุมัติจากหัวหน้า อนุมัติแล้วเข้าใช้ได้ทันที',
    disabled: 'บัญชีนี้ถูกปิดใช้งาน',
    locked: 'ใส่รหัสผ่านผิดหลายครั้ง ลองใหม่อีก {{minutes}} นาที หรือขอลิงก์ตั้งรหัสผ่านใหม่จากหัวหน้า',
    tooMany: 'ลองหลายครั้งเกินไป รอสักครู่แล้วลองใหม่',
  },
  photo: {
    take: 'ถ่ายรูป',
    fromLibrary: 'เลือกจากคลังรูป',
    choose: 'เลือกรูป',
    change: 'เปลี่ยนรูป',
    hint: 'หน้าตรง เห็นหน้าชัด ไม่สวมหมวกหรือแว่นดำ',
    unreadable: 'เปิดรูปนี้ไม่ได้ ลองถ่ายใหม่หรือเลือกรูปอื่น',
    required: 'กรุณาถ่ายรูปหน้าตรง',
    mine: 'รูปของฉัน',
    saved: 'เปลี่ยนรูปแล้ว',
  },
  pin: {
    unlockTitle: 'ใส่ PIN',
    unlockSubtitle: 'ใส่ PIN 6 หลักเพื่อเข้าใช้งาน',
    label: 'PIN 6 หลัก',
    erase: 'ลบ',
    wrong: 'PIN ไม่ถูกต้อง เหลืออีก {{count}} ครั้ง',
    signedOut: 'ใส่ PIN ผิด {{count}} ครั้ง ออกจากระบบแล้ว เข้าสู่ระบบด้วยรหัสผ่านเพื่อใช้ต่อ',
    signInAgain: 'เข้าสู่ระบบ',
    forgot: 'ลืม PIN?',
    notYou: 'ไม่ใช่ {{name}}?',
    setTitle: 'ตั้ง PIN',
    setSubtitle: 'ใช้ PIN 6 หลักเปิดแอพบนเครื่องที่เข้าสู่ระบบไว้ ทุกครั้งที่เปิดแอพหรือกลับมาหลังไม่ได้ใช้ {{minutes}} นาที',
    resetTitle: 'ตั้ง PIN ใหม่',
    resetSubtitle: 'ยืนยันด้วยรหัสผ่านของคุณ แล้วตั้ง PIN ใหม่',
    changeTitle: 'เปลี่ยน PIN',
    password: 'รหัสผ่านของคุณ',
    wrongPassword: 'รหัสผ่านไม่ถูกต้อง',
    next: 'ถัดไป',
    enter: 'ตั้ง PIN 6 หลัก',
    confirm: 'ใส่ PIN อีกครั้ง',
    mismatch: 'PIN สองครั้งไม่ตรงกัน ตั้งใหม่อีกครั้ง',
    weak: 'PIN นี้เดาง่ายเกินไป (เลขเดียวกันหรือเรียงกัน) เลือกเลขอื่น',
    saved: 'ตั้ง PIN แล้ว',
    card: 'PIN',
    cardBody: 'ใช้เปิดแอพบนเครื่องที่เข้าสู่ระบบไว้ ทุกครั้งที่เปิดแอพหรือกลับมาหลังไม่ได้ใช้ {{minutes}} นาที ลืม PIN ตั้งใหม่ได้ด้วยรหัสผ่าน',
  },
  // A super admin looking at the apps as someone (apps/api/src/auth/view-as.ts), and where that person sees it
  viewAs: {
    title: 'ดูแบบ {{name}}',
    until: 'ดูอย่างเดียว · ถึง {{time}} น.',
    stop: 'กลับเป็นตัวเอง',
    move: 'ลากไปวางข้างไหนก็ได้',
  },
  signIns: {
    title: 'ประวัติการเข้าสู่ระบบ',
    login: 'เข้าสู่ระบบ',
    viewAs: '{{name}} ดูแอพแบบคุณ (ดูอย่างเดียว)',
    none: 'ยังไม่มีประวัติ',
    hint: '20 ครั้งล่าสุด ถ้ามีครั้งที่ไม่ใช่คุณ ให้เปลี่ยนรหัสผ่านและแจ้งหัวหน้าหรือ HR ทันที',
  },
  prefs: {
    title: 'Preference',
    language: 'ภาษา',
    theme: 'ธีม',
    light: 'สว่าง',
    dark: 'มืด',
    auto: 'ตามอุปกรณ์',
    density: 'ความหนาแน่นของตาราง',
    comfortable: 'สบายตา',
    compact: 'กระชับ',
    textSize: 'ขนาดตัวอักษร',
    small: 'เล็ก',
    medium: 'ปกติ',
    large: 'ใหญ่',
    profile: 'บัญชีของฉัน',
  },
  pwa: {
    install: 'ติดตั้งแอพ',
    updateReady: 'มีเวอร์ชันใหม่ของแอพ',
    reload: 'โหลดใหม่',
    push: 'การแจ้งเตือนบนอุปกรณ์นี้',
    pushOn: 'งานใหม่และเรื่องที่ต้องทำจะเด้งขึ้นบนอุปกรณ์นี้ แม้ไม่ได้เปิดแอพ',
    pushOff: 'เปิดไว้เพื่อรู้ทันทีเมื่อมีงานใหม่หรืองานมีการเปลี่ยนแปลง',
    pushDenied: 'เบราว์เซอร์ปิดการแจ้งเตือนของแอพนี้ไว้ เปิดได้ที่การตั้งค่าเว็บไซต์ในเบราว์เซอร์',
    pushInstall: 'บน iPhone และ iPad ต้องเพิ่มแอพลงหน้าจอโฮมก่อน: เปิดใน Safari แตะปุ่มแชร์ แล้วเลือก “เพิ่มไปยังหน้าจอโฮม”',
    pushUnsupported: 'เบราว์เซอร์นี้ไม่รองรับการแจ้งเตือน ลองเปิดใน Chrome หรือ Safari',
    pushFailed: 'เปิดการแจ้งเตือนไม่สำเร็จ ลองอีกครั้ง',
    pushTest: 'ส่งทดสอบ',
    pushTestSent: 'ส่งการแจ้งเตือนทดสอบแล้ว',
    promptTitle: 'รู้ทันทีเมื่อได้งานใหม่',
    promptBody: 'เปิดการแจ้งเตือนบนเครื่องนี้ งานใหม่หรืองานที่เปลี่ยนจะเด้งขึ้นมาแม้ไม่ได้เปิดแอพ',
    promptAction: 'เปิดการแจ้งเตือน',
    later: 'ไว้ทีหลัง',
  },
  // The other apps' windows over Thananan OS on a phone
  /** แชท in Thananan OS (apps/portal/src/chat): staff talking to each other */
  chat: {
    title: 'แชท',
    search: 'ค้นหาแชท',
    empty: 'ยังไม่มีแชท เริ่มคุยกับเพื่อนร่วมงานได้จากรายชื่อ',
    none: 'ไม่พบแชทที่ค้นหา',
    pick: 'เลือกแชททางซ้ายเพื่อเริ่มคุย',
    start: 'แชท',
    newGroup: 'สร้างกลุ่ม',
    groupName: 'ชื่อกลุ่ม',
    pickPeople: 'เลือกคน',
    create: 'สร้าง',
    kinds: { direct: 'ส่วนตัว', group: 'กลุ่ม', branch: 'กลุ่มสาขา' },
    you: 'คุณ',
    gone: 'ลาออกแล้ว',
    sentFiles: 'ส่งไฟล์',
    noMessages: 'ยังไม่มีข้อความ ทักทายกันได้เลย',
    placeholder: 'พิมพ์ข้อความ',
    send: 'ส่ง',
    attach: 'แนบรูปหรือไฟล์',
    sending: 'กำลังส่ง',
    failed: 'ส่งไม่สำเร็จ',
    retry: 'ส่งอีกครั้ง',
    unsent: 'ยกเลิกข้อความแล้ว',
    edited: 'แก้ไขแล้ว',
    forwarded: 'ส่งต่อ',
    read: 'อ่านแล้ว',
    readBy: 'อ่าน {{count}}',
    reply: 'ตอบกลับ',
    replyingTo: 'ตอบ {{name}}',
    copy: 'คัดลอก',
    copied: 'คัดลอกแล้ว',
    forward: 'ส่งต่อ',
    forwardTo: 'ส่งต่อไปที่',
    forwardedTo: 'ส่งต่อไปที่ {{name}} แล้ว',
    edit: 'แก้ไข',
    editing: 'กำลังแก้ไขข้อความ',
    unsend: 'ยกเลิกส่ง',
    unsendTitle: 'ยกเลิกส่งข้อความนี้?',
    unsendBody: 'ข้อความและไฟล์จะหายไปจากห้อง ทุกคนเห็นว่ายกเลิกแล้ว',
    pin: 'ปักเป็นประกาศ',
    unpin: 'เลิกปัก',
    announcement: 'ประกาศ',
    info: 'ข้อมูลห้อง',
    members: '{{count}} คน',
    mute: 'ปิดการแจ้งเตือน',
    muteHint: 'ข้อความที่ @ถึงคุณยังแจ้งเตือน',
    rename: 'เปลี่ยนชื่อกลุ่ม',
    add: 'เพิ่มคน',
    admin: 'ผู้ดูแล',
    remove: 'นำออกจากกลุ่ม',
    leave: 'ออกจากกลุ่ม',
    leaveTitle: 'ออกจากกลุ่มนี้?',
    leaveBody: 'จะไม่เห็นข้อความของกลุ่มอีก จนกว่าผู้ดูแลเพิ่มกลับเข้ามา',
  },
  /** Sharing one's own with others at the company (Drive's folders, Boards' boards): `GrantsEditor` in app-kit */
  sharing: {
    kinds: { user: 'คน', branch: 'สาขา', department: 'แผนก', company: 'ทั้งบริษัท' },
    add: 'เพิ่มคน สาขา แผนก หรือทั้งบริษัท',
    people: 'ผู้มีสิทธิ์',
    role: 'สิทธิ์',
    remove: 'เอาสิทธิ์ออก',
    owner: 'เจ้าของ',
  },
  shell: {
    search: 'ค้นหา',
    searchPlaceholder: 'ค้นหาหรือพิมพ์คำสั่ง',
    help: 'ช่วยเหลือ',
    settings: 'ตั้งค่า',
    notifications: 'การแจ้งเตือน',
    menu: 'เมนู',
    collapse: 'ย่อเมนู',
    expand: 'ขยายเมนู',
    goTo: 'ไปที่',
    commands: 'คำสั่ง',
    recent: 'ล่าสุด',
    /**
     * Thananan OS's front page with one's own pages, its staff directory and the apps, on its bottom bar; the front page's
     * day at a glance (guides name them too)
     */
    contacts: 'รายชื่อ',
    home: 'หน้าแรก',
    apps: 'แอพ',
    overview: 'ภาพรวม',
    switchApp: 'เปลี่ยนแอพ',
    askApps: 'ขอใช้แอพอื่น',
  },
  /** What waits for one to decide or do (`/api/me/approvals`): Thananan OS's รอคุณ, the numbers on each app's menu */
  waiting: {
    access_requests: 'คำขอเปิดบัญชีและขอใช้แอพ',
    hr_applications: 'ใบสมัครงานรอตรวจ',
    hr_leaves: 'ใบลา',
    hr_corrections: 'ขอแก้เวลา',
    cash_variances: 'เงินขาด/เกินรออนุมัติ',
    cash_deposits: 'ใบนำฝากรอตรวจ',
    cash_expenses: 'เงินสดย่อยที่ใช้เงินต้องนำส่ง รอตรวจ',
    cash_claims: 'เบิกค่าใช้จ่ายรออนุมัติ',
    cash_claimsToPay: 'เบิกค่าใช้จ่ายรอโอนคืน',
    payroll_runs: 'งวดเงินเดือนรออนุมัติ',
    payroll_advances: 'ขอเบิกเงินล่วงหน้า',
    accounting_invoicesUnpaid: 'ใบแจ้งหนี้ยังไม่ชำระ',
    accounting_invoicesPartial: 'ใบแจ้งหนี้ชำระบางส่วน',
    accounting_bills: 'ใบตั้งหนี้รออนุมัติ',
    accounting_billsToPay: 'ใบตั้งหนี้รอจ่าย',
    accounting_advances: 'เบิกล่วงหน้ารอส่งธนาคาร',
    accounting_transfers: 'รอโอนในธนาคาร',
    tms_refunds: 'ใบคืนเงินรออนุมัติ',
    company_handovers: 'ทรัพย์สินรอคุณยืนยันรับ',
    company_returns: 'ของเสียหาย/สูญหายรอบัญชีตรวจ',
  },
  guides: {
    title: 'คู่มือ',
    open: 'คู่มือหน้านี้',
    all: 'คู่มือทั้งหมด',
    search: 'ค้นหาคู่มือ',
    none: 'ไม่พบคู่มือที่ตรงกับคำค้น',
    notWritten: 'ยังไม่มีคู่มือที่เขียนไว้สำหรับหน้านี้',
    youMay: 'สิ่งที่คุณทำได้',
    statuses: 'สถานะ',
    lifecycle: 'ขั้นตอนของเอกสาร',
    kinds: { task: 'งาน', topic: 'หัวข้อ', page: 'คู่มือหน้า' },
    document: 'ร่าง: บันทึกแล้วแต่ยังไม่มีผล แก้และลบได้ → เสนอแล้ว: ได้เลขที่และมีผล แก้ได้เฉพาะบางช่อง → ยกเลิก: เก็บไว้เป็นประวัติ แก้ไขเป็นฉบับใหม่ได้',
  },
  time: { today: 'วันนี้', yesterday: 'เมื่อวาน' },
  units: { km: 'กม.', kg: 'กก.', baht: 'บาท', pallets: 'พาเลท', minutes: 'นาที' },
} as const

type Widen<T> = { [K in keyof T]: T[K] extends string ? string : Widen<T[K]> }
export type CommonText = Widen<typeof th>

export const en: CommonText = {
  apps: {
    portal: 'Thananan OS',
    tms: 'TMS',
    accounting: 'Accounting',
    hr: 'HR',
    payroll: 'Payroll',
    admin: 'Admin',
    company: 'Company',
    driver: 'Driver',
    drive: 'Drive',
    boards: 'Boards',
    cash: 'Cash',
    access: 'Access',
    switcher: 'All apps',
    other: 'Other apps',
  },
  actions: {
    save: 'Save',
    saveAndNew: 'Save and new',
    cancel: 'Cancel',
    close: 'Close',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    duplicate: 'Duplicate',
    view: 'View',
    back: 'Back',
    more: 'More',
    search: 'Search',
    filter: 'Filter',
    clearFilters: 'Clear filters',
    refresh: 'Refresh',
    import: 'Import',
    export: 'Export',
    print: 'Print',
    retry: 'Try again',
    confirm: 'Confirm',
    columns: 'Columns',
    selectAll: 'Select all',
    done: 'Done',
    next: 'Next',
    previous: 'Previous',
    openMenu: 'Open menu',
  },
  status: {
    loading: 'Loading…',
    saving: 'Saving…',
    saved: 'Saved',
    created: '{{name}} created',
    updated: '{{name}} saved',
    deleted: '{{name}} deleted',
    offline: 'You are offline',
    demo: 'Demo data',
  },
  empty: {
    title: 'No {{name}} yet',
    body: 'Create the first one or import from an Excel file',
    bodyCreate: 'What you create shows up here',
    noResults: 'Nothing matches these filters',
    noResultsBody: 'Try another search or clear the filters',
  },
  errors: {
    generic: 'Something went wrong. Please try again.',
    network: 'Cannot reach the server. Check your connection and try again.',
    forbidden: 'You do not have permission to do this',
    forbiddenApp: 'Your account cannot use this app yet. Ask an administrator for access.',
    notFound: 'This record was not found',
    validation: 'Please check the highlighted fields',
    conflict: 'This record already exists',
    pageNotFound: 'Page not found',
  },
  confirm: {
    deleteTitle: 'Delete {{name}}?',
    deleteBody: '{{label}} will be deleted permanently.',
    leaveTitle: 'Unsaved changes',
    leaveBody: 'Your changes will be lost. Leave this page?',
    leave: 'Leave without saving',
    stay: 'Stay',
  },
  list: {
    range: '{{from}}–{{to}} of {{total}}',
    rowsPerPage: 'Rows per page',
    selected: '{{count}} selected',
    all: 'All',
    searchPlaceholder: 'Search {{name}}',
    density: 'Density',
    resetColumns: 'Reset columns',
    dateRange: 'Date range',
    today: 'Today',
    selectRow: 'Select row {{n}}',
    last7Days: 'Last 7 days',
    between: 'Between',
    allDates: 'All dates',
    previousPage: 'Previous page',
    nextPage: 'Next page',
  },
  banks: {
    KBANK: 'Kasikornbank',
    SCB: 'Siam Commercial Bank',
    BBL: 'Bangkok Bank',
    KTB: 'Krungthai Bank',
    BAY: 'Krungsri',
    TTB: 'TMBThanachart',
    GSB: 'Government Savings Bank',
    BAAC: 'BAAC',
    GHB: 'Government Housing Bank',
    UOB: 'UOB',
    CIMB: 'CIMB Thai',
    KKP: 'Kiatnakin Phatra',
    LHB: 'Land and Houses Bank',
    TISCO: 'TISCO',
    ICBC: 'ICBC (Thai)',
  },
  party: {
    juristic: 'Company',
    individual: 'Individual',
    type: 'Type',
    noWithholding: 'An individual cannot withhold tax',
    idCard: 'National ID',
  },
  doc: {
    fields: {
      docstatus: 'Document status',
      amendedFrom: 'Amended from',
      submittedAt: 'Submitted at',
      submittedBy: 'Submitted by',
      cancelledAt: 'Cancelled at',
      cancelledBy: 'Cancelled by',
      cancelReason: 'Cancel reason',
    },
    status: { draft: 'Draft', submitted: 'Submitted', cancelled: 'Cancelled' },
    selfApproved: 'Self-approved',
    draftTitle: '{{name}} (draft)',
    actions: {
      saveDraft: 'Save draft',
      submit: 'Submit',
      saveAndSubmit: 'Save and submit',
      cancel: 'Cancel document',
      amend: 'Amend',
      editAfterSubmit: 'Edit after submit',
      stopEditing: 'Discard changes',
    },
    confirm: {
      submitTitle: 'Submit {{name}}?',
      submitBody: 'Submitting numbers the document and locks it. To correct it later, cancel and amend.',
      cancelTitle: 'Cancel {{name}} {{label}}?',
      cancelBody: 'The document stays on record, its effects are undone, and it can be amended into a new one.',
      reason: 'Reason for cancelling',
      reasonRequired: 'Please give a reason',
      submitManyTitle: 'Submit {{count}} {{name}}?',
    },
    done: {
      submit: '{{name}} {{label}} submitted',
      cancel: '{{name}} {{label}} cancelled',
      amend: 'New draft created from {{label}}',
      submitMany: '{{count}} submitted',
      submitManyFailed: '{{done}} submitted, {{failed}} refused: {{reason}}',
    },
    banner: {
      draft: 'Draft: no effect yet. Check it, then submit.',
      submitted: 'Submitted {{at}} by {{by}}. To correct it, cancel and amend.',
      submittedEditable: 'Submitted {{at}} by {{by}}. To change it, use "Edit after submit"; every change is recorded.',
      editingAfterSubmit: 'Editing after submit: {{fields}} can change. Every change is recorded and counted as an edit after submit.',
      allFields: 'every field',
      cancelled: 'Cancelled {{at}} by {{by}}',
      reason: 'Reason: {{reason}}',
      amendedFrom: 'Amended from',
    },
  },
  file: {
    attach: 'Attach a photo or file',
    view: 'View file',
    remove: 'Remove file',
    tooBig: 'The file is over 10 MB',
    failed: 'Upload failed, try again',
    uploading: 'Uploading…',
  },
  task: {
    thisDevice: 'Choose a file on this device',
    takeOrPick: 'Take a photo or choose a file',
    askSomeone: 'Ask someone…',
    signature: 'Sign',
    askTitle: 'Ask someone: {{label}}',
    person: 'Ask',
    personPlaceholder: 'Type a name to search',
    note: 'A note to them (optional)',
    askHint: 'If they take your tasks automatically they are notified at once; otherwise it waits in their requests until they accept it',
    send: 'Send',
    waitingOther: 'Waiting for {{name}}',
    requested: 'Sent to {{name}}: waiting for them to accept',
    declined: '{{name}} declined',
    cancelled: 'Cancelled',
    expired: 'The request expired: ask again',
    done: 'Received',
    cancel: 'Cancel',
    dismiss: 'OK',
    failed: 'Could not send the request: try again',
    work: { camera: 'Take a photo', choose: 'Choose photos or files', send: 'Send ({{count}})', sendSignature: 'Send the signature' },
  },
  screen: {
    name: 'Portable screen',
    pair: 'Pair a portable screen',
    pairTitle: 'Pair a portable screen',
    pairHint: 'Open the portable screen app ({{url}}) on a tablet or phone, then scan this QR or type the code below',
    scanThis: 'QR to pair a portable screen',
    expiresIn: 'Good for {{seconds}} more seconds',
    spent: 'This code has expired',
    newCode: 'New code',
    replaces: 'Your screen now ({{name}}) is unpaired when a new one pairs',
    useThisDevice: 'Use this device as my screen',
    pairedWith: '{{name}} is your portable screen',
    failed: 'Pairing failed: try again',
    attachThere: 'Take it on my screen',
    pairToAttach: 'Take it on my screen…',
    signThere: 'Sign on my screen',
    waiting: 'Waiting on your screen',
    waitingSign: 'Sent to your screen: waiting for the signature',
    waitingOn: 'Showing on {{name}}',
    closed: '{{name}} is not open: it shows once it is',
    doHere: 'Do it here instead',
    signHere: 'Sign on this screen instead',
    again: 'Ask again on my screen',
    device: { androidPhone: 'Android phone', androidTablet: 'Android tablet', computer: 'Computer' },
    pairHere: 'Pair this screen',
    pairHereHint:
      'On your computer, press "{{button}}" (a branch\'s clock screen: "{{device}}" on that screen in the HR app), then scan the QR with this screen or type the 6-digit code',
    pairDevice: 'Pair a device',
    clockQr: 'Clock-in QR',
    clockHint: "Scan with your phone's camera to clock in or out",
    clockOnce: 'Each QR works once and changes as soon as someone scans it',
    clockedIn: '{{name}} clocked in at {{time}}',
    clockedOut: '{{name}} clocked out at {{time}}',
    scan: 'Scan the QR',
    code: '6-digit code',
    orCode: 'or type the 6-digit code',
    pairButton: 'Pair',
    noCamera: 'The camera did not open: type the code instead',
    yours: "{{name}}'s portable screen",
    welcome: 'Welcome',
    idle: 'Documents to sign and photos to take show here',
    tapFullscreen: 'Tap for full screen',
    fullscreen: 'Full screen',
    exitFullscreen: 'Exit full screen',
    confirm: 'Confirm signature',
    thanks: 'Thank you, received',
    more: '{{count}} more waiting',
    leave: 'Unpair this screen',
    leaveConfirm: 'Unpair this screen from {{name}}? It has to be scanned again to be used',
    hint: 'A tablet or phone where customers sign and photos for documents are taken while you work at the computer',
    none: 'No screen paired',
    pairedAt: 'Paired {{at}}',
    online: 'Open',
    offline: 'Not open',
    unpair: 'Unpair',
  },
  activity: {
    title: 'Activity',
    comment: 'Comment',
    placeholder: 'Write a comment… (Ctrl + Enter to send)',
    send: 'Send comment',
    edited: 'edited',
    deleteTitle: 'Delete this comment?',
    deleteBody: 'It cannot be restored.',
    listChanged: 'list changed',
    afterSubmit: 'after submit',
    via: '(through {{name}})',
    showAll: 'Show all ({{count}})',
    editedAfterSubmit: 'Edited after submit: {{count}} field(s)',
    editedAfterSubmitTitle: '{{count}} field(s) changed after submit, in {{edits}} edit(s)',
  },
  form: {
    required: 'Required',
    optional: 'Optional',
    selectPlaceholder: 'Select',
    searchPlaceholder: 'Type to search',
    suggestHint: 'Type anything or pick a previous value',
    area: 'Subdistrict / district / province',
    province: 'Province',
    areaHint: 'Type a subdistrict, district, province or postcode',
    coverageHint: 'Type a province or district',
    excludedHint: 'Type a district or subdistrict',
    noOptions: 'No options',
    yes: 'Yes',
    no: 'No',
    newRecord: 'New {{name}}',
    editRecord: 'Edit {{name}}',
    step: 'Step {{step}} of {{steps}}',
    toReview: 'Back to review',
    copiedFrom: 'A copy of {{label}}, not saved yet',
    taken: 'This {{field}} is already on',
    takenMore: 'and {{count}} more',
  },
  import: {
    title: 'Import {{name}}',
    stepUpload: 'Choose file',
    stepMap: 'Match columns',
    stepReview: 'Review',
    stepDone: 'Done',
    drop: 'Drop an Excel or CSV file here',
    dropHint: 'or click to choose (.xlsx, .csv, up to 5,000 rows)',
    template: 'Download template',
    column: 'Column in file',
    field: 'Field',
    ignore: 'Skip',
    mode: 'Import mode',
    modeCreate: 'Add all rows as new records',
    modeUpsert: 'Update existing records (matched by {{key}})',
    rows: '{{count}} rows',
    valid: '{{count}} rows ready',
    invalid: '{{count}} rows have problems',
    row: 'Row {{row}}',
    fixAndRetry: 'Fix the file and upload again, or import only the valid rows',
    importValid: 'Import {{count}} rows',
    result: '{{created}} added, {{updated}} updated',
    unsupported: 'This file cannot be read. Use .xlsx or .csv.',
    allOrNothing: 'Fix every row first: they are saved all together',
    checking: 'Checking…',
    imported: '{{count}} imported',
    chooseFile: 'Choose an Excel file',
  },
  export: {
    title: 'Export {{name}}',
    excel: 'Excel (.xlsx)',
    csv: 'CSV (.csv)',
    scopeFiltered: 'Current filters ({{count}})',
    scopeSelected: 'Selected only ({{count}})',
    preparing: 'Preparing file…',
    ready: 'Exported {{count}} records',
  },
  print: {
    title: 'Print',
    list: 'Print list',
    printedAt: 'Printed {{at}}',
    printedBy: 'Printed {{at}} by {{name}}',
    time: 'print {{count}}',
    reprint: 'Reprint {{count}}',
    page: 'Page',
  },
  report: {
    title: 'Reports',
    pick: 'Counts and totals over a date range, grouped and filtered as you like; open a row to see its records',
    noneYours: 'No report you may open yet',
    groupBy: 'Group by',
    byDay: 'By day',
    byMonth: 'By month',
    count: 'Count',
    total: 'Total',
    none: 'Not set',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    thisYear: 'This year',
    allFilters: 'All records',
    more: 'The {{count}} largest groups',
    openList: 'See the records of {{name}}',
  },
  scan: { starting: 'Starting the camera… allow it if your phone asks.', close: 'Turn the camera off', open: 'Scan with the camera' },
  signature: {
    mine: 'My signature',
    mineHint:
      'Signs documents here, such as agreeing to have an advance taken from your pay, and the documents you issue. Sign on white paper and photograph it, so it looks like your real signature',
    drawnKept: 'This one was drawn on a screen: change it to a photo of your signature on paper',
    none: 'No signature set',
    set: 'Set my signature',
    change: 'Change',
    remove: 'Remove',
    removeConfirm: 'Remove your signature? What you signed keeps it',
    saved: 'Signature saved',
    removed: 'Signature removed',
    updated: 'Set {{at}}',
    draw: 'Draw',
    photo: 'Photo',
    drawHint: 'Sign in the box with a finger or a pen',
    signHere: 'Sign',
    clear: 'Clear',
    photoTitle: 'A photo of your signature on paper',
    photoTips: 'Sign with a dark pen on white paper and keep it in the middle of the photo: shadows and the paper are taken out',
    choosePhoto: 'Take or choose a photo',
    otherPhoto: 'Another photo',
    cleaning: 'Cleaning up the signature…',
    notFound: 'No signature found: take it again with the signature in the middle and enough light',
    strength: 'Line strength',
    use: 'Use this signature',
    tapToSign: 'Tap to sign',
    signAgain: 'Sign again',
    useMine: 'Use my signature',
    keepAsMine: 'Keep it as my signature: one tap next time',
    method: { stored: 'Kept signature', drawn: 'Drawn', photo: 'From a photo', paper: 'Signed on paper' },
    signing: { signed: 'Signed', unsigned: 'Not signed' },
    via: { app: 'In the app', link: 'By link', device: 'On a staff phone', standing: 'As the authorized signer allows' },
    required: 'Please sign',
    failed: 'The signature was not saved: try again',
    alt: 'Signature',
    date: 'Date',
    reminder: 'You have no signature set: what you issue leaves its line to sign by hand.',
  },
  signing: {
    here: 'Sign here',
    hereHint: 'On this screen, or your paired portable screen',
    link: 'Send a link',
    linkHint: 'They sign on their own phone',
    paper: 'Signed on paper',
    paperHint: 'Keep a photo of the signed page',
    name: 'Signed by',
    title: 'Title',
    needSignature: 'Please sign in the box',
    photo: 'Photograph the signed page',
    photoHint: 'The page with the signature, its document number showing',
    retake: 'Take again',
    needPhoto: 'Photograph the signed page first',
    pagePhoto: 'Photo of the page signed on paper',
    save: 'Save',
    saved: 'Signature saved',
    keepTitle: 'Scan to keep a copy',
    keepHint: 'Scan this QR with a phone to see and download the PDF until {{date}}',
    done: 'Done',
    linkTitle: 'Send a link to sign',
    linkIntro: 'Send the link on LINE or let them scan this QR: they see the document, sign and download the PDF, with no account',
    copy: 'Copy link',
    copied: 'Copied',
    copyMessage: 'Copy message',
    share: 'Share',
    expires: 'The link works until {{date}} and signs once',
    message: '{{company}} sends {{document}} {{number}} for you to sign: see and download it at {{url}}',
    waiting: 'Link sent, not signed yet',
    viewed: 'Opened {{at}}',
    notViewed: 'Not opened yet',
    showLink: 'Show the link',
    revoke: 'Withdraw the link',
    revoked: 'Link withdrawn: it no longer opens',
    changed: 'Changed after signing',
    changedHint: 'The document was changed after it was signed: its history shows what changed',
    onPaper: 'Signed on the paper original',
  },
  auth: {
    signInTitle: 'Sign in',
    signInSubtitle: 'One account for every company app',
    username: 'Username',
    password: 'Password',
    signIn: 'Sign in',
    signOut: 'Sign out',
    invalid: 'Wrong username or password',
    sessionExpired: 'Your session expired. Please sign in again.',
    forgot: 'Forgot your password? Ask your lead for a reset link.',
    noAccount: 'No account yet?',
    signUp: 'Ask for one',
    pending: 'This account is waiting for approval. You can sign in as soon as it is approved.',
    disabled: 'This account is closed',
    locked: 'Too many wrong passwords. Try again in {{minutes}} minutes, or ask your lead for a reset link.',
    tooMany: 'Too many tries. Wait a moment and try again.',
  },
  photo: {
    take: 'Take a photo',
    fromLibrary: 'Choose from photos',
    choose: 'Choose a photo',
    change: 'Change photo',
    hint: 'Face the camera, face clearly visible, no hat or sunglasses',
    unreadable: 'This picture cannot be opened. Take another or choose a different one.',
    required: 'Please take a photo of your face',
    mine: 'My photo',
    saved: 'Photo changed',
  },
  pin: {
    unlockTitle: 'Enter your PIN',
    unlockSubtitle: 'Enter your 6-digit PIN to continue',
    label: '6-digit PIN',
    erase: 'Delete',
    wrong: 'Wrong PIN. {{count}} tries left.',
    signedOut: 'Wrong PIN {{count}} times: you are signed out. Sign in with your password to go on.',
    signInAgain: 'Sign in',
    forgot: 'Forgot your PIN?',
    notYou: 'Not {{name}}?',
    setTitle: 'Set a PIN',
    setSubtitle:
      'Your 6-digit PIN opens the apps on a device you are signed in on, each time you open them or come back after {{minutes}} minutes away',
    resetTitle: 'Set a new PIN',
    resetSubtitle: 'Confirm it’s you with your password, then set a new PIN',
    changeTitle: 'Change PIN',
    password: 'Your password',
    wrongPassword: 'Wrong password',
    next: 'Next',
    enter: 'Choose a 6-digit PIN',
    confirm: 'Enter the PIN again',
    mismatch: 'The two PINs differ. Choose one again.',
    weak: 'Too easy to guess (one digit, or digits in a row). Choose another.',
    saved: 'PIN set',
    card: 'PIN',
    cardBody:
      'Opens the apps on a device you are signed in on, each time you open them or come back after {{minutes}} minutes away. Forgot it? Set a new one with your password.',
  },
  viewAs: {
    title: 'Viewing as {{name}}',
    until: 'Read only · until {{time}}',
    stop: 'Back to me',
    move: 'Drag it to either side',
  },
  signIns: {
    title: 'Sign-in history',
    login: 'Signed in',
    viewAs: '{{name}} viewed the apps as you (read only)',
    none: 'Nothing yet',
    hint: 'The last 20. If one was not you, change your password and tell your lead or HR now.',
  },
  prefs: {
    title: 'Preferences',
    language: 'Language',
    theme: 'Theme',
    light: 'Light',
    dark: 'Dark',
    auto: 'Device',
    density: 'Table density',
    comfortable: 'Comfortable',
    compact: 'Compact',
    textSize: 'Text size',
    small: 'Small',
    medium: 'Normal',
    large: 'Large',
    profile: 'My account',
  },
  pwa: {
    install: 'Install app',
    updateReady: 'A new version of the app is ready',
    reload: 'Reload',
    push: 'Notifications on this device',
    pushOn: 'New jobs and things to do pop up on this device, even when the app is closed',
    pushOff: 'Turn on to hear right away about new jobs and changes',
    pushDenied: 'The browser blocks notifications for this app. Allow them in the browser’s site settings',
    pushInstall: 'On iPhone and iPad, add the app to the Home Screen first: open it in Safari, tap Share, then “Add to Home Screen”',
    pushUnsupported: 'This browser cannot show notifications. Try Chrome or Safari',
    pushFailed: 'Could not turn on notifications. Try again',
    pushTest: 'Send a test',
    pushTestSent: 'Test notification sent',
    promptTitle: 'Hear about new jobs right away',
    promptBody: 'Turn on notifications on this device: new and changed jobs pop up even when the app is closed',
    promptAction: 'Turn on notifications',
    later: 'Not now',
  },
  chat: {
    title: 'Chat',
    search: 'Search chats',
    empty: 'No chats yet. Start one with a colleague from Contacts',
    none: 'No chat matches',
    pick: 'Pick a chat on the left to start talking',
    start: 'Chat',
    newGroup: 'New group',
    groupName: 'Group name',
    pickPeople: 'Pick people',
    create: 'Create',
    kinds: { direct: 'Direct', group: 'Group', branch: 'Branch group' },
    you: 'You',
    gone: 'Has left',
    sentFiles: 'Sent a file',
    noMessages: 'No messages yet. Say hello',
    placeholder: 'Type a message',
    send: 'Send',
    attach: 'Attach a photo or file',
    sending: 'Sending',
    failed: 'Not sent',
    retry: 'Send again',
    unsent: 'Message unsent',
    edited: 'Edited',
    forwarded: 'Forwarded',
    read: 'Read',
    readBy: 'Read by {{count}}',
    reply: 'Reply',
    replyingTo: 'Replying to {{name}}',
    copy: 'Copy',
    copied: 'Copied',
    forward: 'Forward',
    forwardTo: 'Forward to',
    forwardedTo: 'Forwarded to {{name}}',
    edit: 'Edit',
    editing: 'Editing a message',
    unsend: 'Unsend',
    unsendTitle: 'Unsend this message?',
    unsendBody: 'Its text and files leave the room; everyone sees it was unsent',
    pin: 'Pin as announcement',
    unpin: 'Unpin',
    announcement: 'Announcement',
    info: 'Chat info',
    members: '{{count}} people',
    mute: 'Mute notifications',
    muteHint: 'Messages that @mention you still notify',
    rename: 'Rename group',
    add: 'Add people',
    admin: 'Admin',
    remove: 'Remove from group',
    leave: 'Leave group',
    leaveTitle: 'Leave this group?',
    leaveBody: 'You won’t see its messages until an admin adds you back',
  },
  sharing: {
    kinds: { user: 'Person', branch: 'Branch', department: 'Department', company: 'Whole company' },
    add: 'Add a person, branch, department or the whole company',
    people: 'Who has access',
    role: 'Access',
    remove: 'Remove access',
    owner: 'Owner',
  },
  shell: {
    search: 'Search',
    searchPlaceholder: 'Search or type a command',
    help: 'Help',
    settings: 'Settings',
    notifications: 'Notifications',
    menu: 'Menu',
    collapse: 'Collapse menu',
    expand: 'Expand menu',
    goTo: 'Go to',
    commands: 'Commands',
    recent: 'Recent',
    contacts: 'Contacts',
    home: 'Home',
    apps: 'Apps',
    overview: 'Overview',
    switchApp: 'Change app',
    askApps: 'Ask for another app',
  },
  waiting: {
    access_requests: 'Account and app requests',
    hr_applications: 'Job applications to check',
    hr_leaves: 'Leave',
    hr_corrections: 'Time corrections',
    cash_variances: 'Cash shortages/surpluses',
    cash_deposits: 'Deposits to verify',
    cash_expenses: 'Expenses paid from collections',
    cash_claims: 'Expense claims to approve',
    cash_claimsToPay: 'Expense claims to pay back',
    payroll_runs: 'Pay runs to approve',
    payroll_advances: 'Salary advances',
    accounting_invoicesUnpaid: 'Unpaid invoices',
    accounting_invoicesPartial: 'Partly paid invoices',
    accounting_bills: 'Bills to approve',
    accounting_billsToPay: 'Bills to pay',
    accounting_advances: 'Advances to put in the bank',
    accounting_transfers: 'In the bank, to transfer',
    tms_refunds: 'Refunds to approve',
    company_handovers: 'Company items to confirm',
    company_returns: 'Damaged or lost items to review',
  },
  guides: {
    title: 'Guide',
    open: 'Guide to this page',
    all: 'All guides',
    search: 'Search guides',
    none: 'No guide matches',
    notWritten: 'This page has no written guide yet',
    youMay: 'What you can do',
    statuses: 'Statuses',
    lifecycle: 'How a document goes',
    kinds: { task: 'Tasks', topic: 'Topics', page: 'Page guides' },
    document:
      'Draft: saved, no effect yet, can be edited or deleted → Submitted: numbered and in effect, only some fields change → Cancelled: kept as history, can be amended into a new one',
  },
  time: { today: 'Today', yesterday: 'Yesterday' },
  units: { km: 'km', kg: 'kg', baht: 'THB', pallets: 'pallets', minutes: 'min' },
}
